import React from 'react';
import lI from 'lodash-inflection';
import { Button, } from 'reactstrap';
import { useToggle, } from 'react-use';
import { fromPairs, } from 'lodash';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable } from 'array-move';
import { toast } from 'react-toastify';
import classnames from 'classnames';

import firebase from '../firebase';
import { getCollectionData, } from '../shared/firebase';
import { fields, modelTypes, composeChangeFieldNames, hasChangeFieldValue, } from '../shared/models/changeField';
import useDocumentSubscription from './hooks/useDocumentSubscription';
import SimpleModal from './modals/SimpleModal';
import ModelFormModal from './modals/ModelFormModal';
import AddButton from './AddButton';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';

const db = firebase.firestore();

const fixedLabelsOf = (modelType) => {
  try {
    return require(`../shared/models/${modelType}`).fixedChangeFieldLabels || {};
  } catch (e) {
    return {};
  }
};

export default function ChangeFieldsModalButton (props) {
  const { company, user, modelType, customChangeFields, savedOrder, isParent, } = props;
  const [showsModal, toggleModal] = useToggle(false);
  const [isDragging, toggleDragging] = useToggle(false);
  const orderDoc = useDocumentSubscription(company.ref.collection('changeFieldOrders').doc(modelType), [company.id, modelType]);
  const hiddenKeys = orderDoc?.hiddenKeys || [];
  const { role, } = company?.users?.[user?.id] || {};
  const canManage = user?.admin || ['owner', 'admin', 'member'].includes(role);
  if (!canManage) return null;
  if (!isParent) return null;

  const collectionName = lI.pluralize(modelType);
  const changeFieldsRef = company.ref.collection('changeFields');
  const orderDocRef = company.ref.collection('changeFieldOrders').doc(modelType);
  const checkFieldUsage = async (fieldKey, { checksValue = true, checksJournals = true, } = {}) => {
    const changeType = `${modelType}__${fieldKey}`;
    const [accountItems, presetSettings, items, individualAdjustmentJournals, consolidationJournals] = await Promise.all([
      getCollectionData(company.ref.collection('accountItems')),
      getCollectionData(company.ref.collection('presetConsolidationAccountItemSettings')),
      checksValue ? getCollectionData(company.ref.collection(collectionName)) : [],
      checksJournals ? getCollectionData(company.ref.collection('individualAdjustmentJournals').where('changeTypes', 'array-contains', changeType)) : [],
      checksJournals ? getCollectionData(company.ref.collection('consolidationJournals').where('changeTypes', 'array-contains', changeType)) : [],
    ]);
    const usedInCfMapping = [...accountItems, ...presetSettings]
      .flatMap(_ => _.cfMappingItems || [])
      .some(_ => _.pkgCategory === collectionName && _.columnKey === fieldKey);
    const hasValue = hasChangeFieldValue(items, fieldKey);
    const usedInJournals = individualAdjustmentJournals.length > 0 || consolidationJournals.length > 0;
    return { usedInCfMapping, hasValue, usedInJournals };
  };
  const beforeDeleteCustomField = (customField) => async () => {
    const { usedInCfMapping, hasValue, usedInJournals } = await checkFieldUsage(customField.id);
    if (usedInCfMapping) {
      toast.error('CFマッピングで設定されているため、削除できません');
      return false;
    }
    if (hasValue) {
      toast.error('数値が入力されているため、削除できません');
      return false;
    }
    if (usedInJournals) {
      toast.error('仕訳で使用されているため、削除できません');
      return false;
    }
  };
  // 仕訳で使用中でも非表示にできる(既に選択済みの仕訳の編集フォームでは選択肢として残る)
  const onClickHide = (key) => async () => {
    const { usedInCfMapping } = await checkFieldUsage(key, { checksValue: false, checksJournals: false, });
    if (usedInCfMapping) {
      toast.error('CFマッピングで設定されているため、非表示にできません');
      return;
    }
    await orderDocRef.set({ hiddenKeys: [...hiddenKeys, key], updatedAt: new Date(), }, { merge: true });
  };
  const onClickShow = (key) => async () => {
    await orderDocRef.set({ hiddenKeys: hiddenKeys.filter(_ => _ !== key), updatedAt: new Date(), }, { merge: true });
  };
  const fixedLabels = fixedLabelsOf(modelType);
  const customLabelsById = fromPairs((customChangeFields || []).map(_ => [_.id, _.label]));
  const orderedKeys = composeChangeFieldNames(fixedLabels, customChangeFields, savedOrder, hiddenKeys).filter(_ => _ !== 'other');
  const hiddenOrderedKeys = composeChangeFieldNames(fixedLabels, customChangeFields, savedOrder).filter(_ => _ !== 'other' && hiddenKeys.includes(_));
  const customByIdMap = fromPairs((customChangeFields || []).map(_ => [_.id, _]));
  const existingLabels = [...Object.values(fixedLabels), ...(customChangeFields || []).map(_ => _.label)];
  const onDrop = async ({ addedIndex, removedIndex }) => {
    if (addedIndex == null || removedIndex == null) return;
    const newKeys = [...arrayMoveImmutable(orderedKeys, removedIndex, addedIndex), ...hiddenOrderedKeys];
    await orderDocRef.set({ keys: newKeys, updatedAt: new Date(), }, { merge: true });
  };

  return (
    <>
      <Button color="secondary" outline size="sm" onClick={_ => toggleModal(true)} data-operation-type="write">
        <span className="fas fa-fw fa-cog mr-1" />
        項目を管理
      </Button>
      {
        showsModal && (
          <SimpleModal isOpen title={`${modelTypes[modelType]?.label} 項目`} onClickClose={_ => toggleModal(false)} size="lg">
            <div className="d-flex justify-content-end mb-2">
              <AddButton
                size="sm"
                itemRef={changeFieldsRef.doc()}
                FormModal={ModelFormModal}
                processValues={_ => ({ ..._, modelType, updatedAt: new Date(), })}
                formProps={{ title: '項目の追加', fields: fields({ existingLabels, }), }}
              />
            </div>
            <Container
              dragHandleSelector=".drag-handle"
              onDrop={onDrop}
              onDragStart={_ => toggleDragging(true)}
              onDragEnd={_ => toggleDragging(false)}
            >
              {
                orderedKeys.map((key) => {
                  const isPreset = fixedLabels[key] != null;
                  const label = isPreset ? fixedLabels[key] : customLabelsById[key];
                  const custom = customByIdMap[key];
                  return (
                    <Draggable key={key}>
                      <div
                        className={classnames('d-flex align-items-center px-3 py-2', { 'bg-light': isPreset, 'bg-white': !isPreset })}
                        style={{ border: '1px solid #dee2e6', borderRadius: '0.25rem', marginBottom: '0.5rem', }}
                      >
                        <div className="drag-handle text-muted mr-3" style={{ cursor: 'grab' }}>
                          <span className="fas fa-grip-vertical" />
                        </div>
                        <div className="flex-grow-1">
                          {label}
                        </div>
                        <div className="d-flex gap-1">
                          <Button size="sm" color="secondary" outline onClick={onClickHide(key)} title="非表示にする">
                            <span className="fas fa-eye-slash" />
                          </Button>
                          {
                            !isPreset && custom && (
                              <>
                                <EditButton
                                  size="sm"
                                  itemRef={custom.ref}
                                  FormModal={ModelFormModal}
                                  processValues={_ => ({ ..._, updatedAt: new Date(), })}
                                  formProps={{ title: '項目の編集', fields: fields({ existingLabels: existingLabels.filter(_ => _ !== label), }), }}
                                />
                                <DeleteButton size="sm" item={custom} itemRef={custom.ref} beforeDelete={beforeDeleteCustomField(custom)} />
                              </>
                            )
                          }
                        </div>
                      </div>
                    </Draggable>
                  );
                })
              }
            </Container>
            <div
              className="d-flex align-items-center px-3 py-2 bg-light"
              style={{ border: '1px solid #dee2e6', borderRadius: '0.25rem', }}
            >
              <div className="text-muted mr-3" style={{ visibility: 'hidden' }}>
                <span className="fas fa-grip-vertical" />
              </div>
              <div className="flex-grow-1">
                その他
              </div>
            </div>
            {
              hiddenOrderedKeys.length > 0 && (
                <div className="mt-4">
                  <div className="text-muted small mb-2">非表示の項目</div>
                  {
                    hiddenOrderedKeys.map((key) => {
                      const isPreset = fixedLabels[key] != null;
                      const label = isPreset ? fixedLabels[key] : customLabelsById[key];
                      const custom = customByIdMap[key];
                      return (
                        <div
                          key={key}
                          className="d-flex align-items-center px-3 py-2 bg-light text-muted"
                          style={{ border: '1px dashed #dee2e6', borderRadius: '0.25rem', marginBottom: '0.5rem', }}
                        >
                          <div className="flex-grow-1">
                            {label}
                          </div>
                          <div className="d-flex gap-1">
                            <Button size="sm" color="secondary" outline onClick={onClickShow(key)} title="表示する">
                              <span className="fas fa-eye" />
                            </Button>
                            {
                              !isPreset && custom && (
                                <DeleteButton size="sm" item={custom} itemRef={custom.ref} beforeDelete={beforeDeleteCustomField(custom)} />
                              )
                            }
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              )
            }
          </SimpleModal>
        )
      }
    </>
  );
};
